import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'

export type MatterArtifactsState = {
  lifecycleGeneration: number
  versionsById: Partial<Record<string, MatterArtifactVersionRead>>
}

const initialState: MatterArtifactsState = {
  lifecycleGeneration: 0,
  versionsById: {},
}

const matterArtifactsSlice = createSlice({
  name: 'matterArtifactsState',
  initialState,
  reducers: {
    startMatterArtifactVersionStatusPoll: (_state, _action: PayloadAction<{ matterArtifactVersionId: string }>) => {},
    upsertMatterArtifactVersion: (state, action: PayloadAction<MatterArtifactVersionRead>) => {
      upsertVersion(state, action.payload)
    },
    upsertMatterArtifactVersions: (state, action: PayloadAction<MatterArtifactVersionRead[]>) => {
      for (const incomingVersion of action.payload) {
        upsertVersion(state, incomingVersion)
      }
    },
    clearMatterArtifacts: (state) => ({ ...initialState, lifecycleGeneration: state.lifecycleGeneration + 1 }),
  },
})

// Heads only advance: every server flow (manual save, agent revision, accepted selected edit)
// creates a strictly newer child version, and a version stops being current only because a sibling
// became current. Snapshots can arrive out of order (a conversation-detail poll captured before a
// save can land after it), so an upsert may carry head bookkeeping older than what the store
// already knows. Two rules keep the newest truth:
// - a promotion loses to an existing head with a strictly newer created_at, or with an equal
//   created_at when that head is the incoming version's direct child (created_at is millisecond-
//   precision, so distinct save round-trips virtually never tie; the child check settles the one
//   conceivable tie without rejecting a user's own newest save);
// - a record demoting the stored head is stale in its entirety (real demotion only happens
//   through a sibling's promotion) and is ignored.
function upsertVersion(state: MatterArtifactsState, incomingVersion: MatterArtifactVersionRead) {
  if (incomingVersion.is_current) {
    if (storeHasNewerHead(state, incomingVersion)) {
      state.versionsById[incomingVersion.id] = { ...incomingVersion, is_current: false }
      return
    }
    for (const version of Object.values(state.versionsById)) {
      if (version?.matter_artifact_id === incomingVersion.matter_artifact_id) {
        version.is_current = version.id === incomingVersion.id
      }
    }
    state.versionsById[incomingVersion.id] = incomingVersion
    return
  }

  if (state.versionsById[incomingVersion.id]?.is_current) {
    // The whole record is stale, not just its flag: a snapshot from before this version became
    // head also predates its final status, and a demoted version never changes fields afterward.
    return
  }
  state.versionsById[incomingVersion.id] = incomingVersion
}

function storeHasNewerHead(state: MatterArtifactsState, incomingVersion: MatterArtifactVersionRead): boolean {
  for (const version of Object.values(state.versionsById)) {
    if (
      version?.matter_artifact_id === incomingVersion.matter_artifact_id &&
      version.id !== incomingVersion.id &&
      version.is_current &&
      (version.created_at > incomingVersion.created_at ||
        (version.created_at === incomingVersion.created_at && version.parent_matter_artifact_version_id === incomingVersion.id))
    ) {
      return true
    }
  }
  return false
}

export const MatterArtifactsActions = matterArtifactsSlice.actions
export default matterArtifactsSlice.reducer
